import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useBooleanFlagValue } from '@openfeature/react-sdk'

import { selectIsFetchingForKeys } from '@/reducers/fetch'
import { fetchSubscriptions } from '@/reducers/subscription.slice'
import { TrialStatus } from '@/reducers/auth/userReducer'
import { sendUserCancellationMessageZap } from '@/actions/zapierActions'
import { fetchBillingPortalSession } from '@/actions/settings/billingActions'
import {
  DOWNLOAD_ACCOUNT_ZIP_KEY,
  downloadAccountInformation,
  fetchCurrentUserDetailsIfNeeded,
} from '@/actions/userActions'
import {
  getCurrentUser,
  getFreeTrialStatus,
  hasManualRequest,
  isCancelling,
  selectIsInUnservicedFreeTrial,
  selectCurrentPlanServices,
} from '@/selectors/user.selectors'

import {
  useFetchResponse,
  useLoadedFlagValue,
  useReselector,
} from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  CancellationStepIdentifier,
  createAutomaticCancellationAnalyticsRecord,
} from '@/features/user/settings/sections/self-serve-cancellation/lib/userCancellation.slice'
import { useGetSaveMoneyAndTimePageData } from '@/features/user/settings/sections/self-serve-cancellation/lib/helpers'
import { FEATURE_FLAG_KEYS } from '@/features/open-feature'
import { usePlanChangeEntry } from '@/features/user/settings/sections/plan-change/lib/usePlanChangeEntry'
import {
  isEligibleForDiscount,
  selectCanSelfServeCancel,
} from '@/features/user/settings/sections/self-serve-cancellation/lib/userCancellation.selectors'
import { useAnalyticsTrack, useAnalyticsView } from '@/features/amplitude'
import {
  fetchUserTransactions,
  USER_TRANSACTION_KEY,
} from '@/features/transactions/transactions.slice'

// TODO: Move to shared tax utils
import {
  fetchUserTaxEstimates,
  ALWAYS_FETCH_USER_TAX_ESTIMATES_KEY,
} from '@/features/taxes/quarterly/userTaxEstimates.slice'

import { Button, Card, Heading, HeadingVariants, Separator, Text } from '@/ui'
import { TypographyStyles } from '@/styles'
import {
  FEATURE_CLIENT_DESCRIPTIONS,
  planServiceLabels,
} from '@/constants/productFeatureConstants'
import { ConfirmManualCancellationDialog } from './ConfirmCancellationDialog'
import { AccountDownloadDialog } from './AccountDownloadDialog'
import { ReactivatePlanDialog } from './ReactivatePlanDialog'
import { InvoiceList } from './InvoiceList'
import { BillingTable } from './BillingTable'
import { PaymentMethodBlock } from './PaymentMethodBlock'

import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { fetchAllAnnualTaxDetailsIfNeeded } from '@/features/admin/annual-tax-details/annualTaxDetails.slice'
import { fetchPayrollProfileIfNeeded } from '@/features/payroll/payrollActions'

import {
  fetchAnnualTaxFilingsIfNeeded,
  getContractorFilingJob,
} from '@/features/taxes/annual-taxes/annualTaxFilings.slice'
import { fetchBankingProfile } from '@/features/banking/bankingSlice'
import {
  fetchAnnualTaxFilingFormsIfNeeded,
  TaxfyleJobStatus,
} from '@/features/taxes/annual-taxes/annualTaxFilingForms.slice'

export const PlansAndBillingCard = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const track = useAnalyticsTrack()
  const pageView = useAnalyticsView()
  const currentTaxYear = useReselector(selectCurrentAnnualTaxYear)
  const membershipIsCancelling = useReselector(isCancelling)
  const membershipHasManualRequest = useReselector(hasManualRequest)
  const currentUser = useReselector(getCurrentUser)
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false)
  const userFreeTrialStatus = useReselector(getFreeTrialStatus)
  const isInUnservicedFreeTrial = useReselector(selectIsInUnservicedFreeTrial)
  const data = useGetSaveMoneyAndTimePageData()
  const contractorFilingJob = useFetchResponse(getContractorFilingJob)

  const downloadingFiles = useReselector(selectIsFetchingForKeys, [
    DOWNLOAD_ACCOUNT_ZIP_KEY,
  ])

  const isLoadingTransactionsAndUserTaxEstimates = useReselector(
    selectIsFetchingForKeys,
    [USER_TRANSACTION_KEY, ALWAYS_FETCH_USER_TAX_ESTIMATES_KEY]
  )

  const isInActiveFreeTrial =
    isInUnservicedFreeTrial && userFreeTrialStatus === TrialStatus.active

  useEffect(() => {
    pageView('plans and billing page')
  }, [pageView])

  const copyFromTier = useLoadedFlagValue(
    FEATURE_FLAG_KEYS.planCopyFromTier,
    false
  )
  const planTierServices = useReselector(selectCurrentPlanServices)
  const grantedLabels = (currentUser?.featurePermissions ?? []).map(
    (fp) => FEATURE_CLIENT_DESCRIPTIONS[fp.id] ?? fp.name
  )
  const planServices = copyFromTier
    ? planServiceLabels({
        featurePermissions: currentUser?.featurePermissions ?? [],
        planTierServices,
      })
    : grantedLabels

  const redirectToStripePortal = useCallback(async () => {
    const res = await fetchBillingPortalSession()(dispatch)
    track('manage stripe plan')

    if (res) {
      window.location.href = res.url
    }
  }, [dispatch, track])

  const downloadZipFile = async () => {
    const result = await downloadAccountInformation(currentUser?.id)(dispatch)
    track('download account zip file')

    if (result) {
      setDownloadDialogOpen(true)
    }
  }

  const isSelfServeCancel = useReselector(
    selectCanSelfServeCancel,
    currentTaxYear
  )
  const contractorJobStatus = contractorFilingJob?.job?.jobStatus
  const hasContractorFilingInProgress =
    contractorJobStatus !== undefined &&
    contractorJobStatus !== TaxfyleJobStatus.completed &&
    contractorJobStatus !== TaxfyleJobStatus.canceled
  const canSelfServeCancel =
    isSelfServeCancel &&
    contractorFilingJob !== undefined &&
    !hasContractorFilingInProgress

  const enableDiscounts = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.enableCancellationDiscounts,
    false
  )

  const { goToPlanChange } = usePlanChangeEntry()

  const eligibleForDiscount = useReselector(
    isEligibleForDiscount,
    enableDiscounts
  )

  const saveFlowCancellationAction = useCallback(async () => {
    const record = await createAutomaticCancellationAnalyticsRecord({
      finalStepReached: CancellationStepIdentifier.SAVING_TIME_AND_MONEY,
      additionalDetails: {
        hasPersonalizedSavingsPage: data.showPersonalizedPage,
        financialAccountConnectionAvailable: data.faAccountAlertType,
        discountAvailable: eligibleForDiscount,
      },
    })(dispatch)

    navigate('/user-cancellation', { state: { recordId: record?.id } })
  }, [dispatch, navigate, data, eligibleForDiscount])

  const handleSelfServeCancellation = () => {
    saveFlowCancellationAction()
    sendUserCancellationMessageZap(currentUser, 'clicked_canceled')
    track('click cancel plan')
  }

  useEffect(() => {
    dispatch(fetchSubscriptions())
    dispatch(fetchPayrollProfileIfNeeded())
    dispatch(fetchUserTransactions())
    dispatch(fetchUserTaxEstimates())
    dispatch(fetchCurrentUserDetailsIfNeeded(true))
    dispatch(fetchAllAnnualTaxDetailsIfNeeded())
    dispatch(fetchAnnualTaxFilingsIfNeeded())
    dispatch(fetchAnnualTaxFilingFormsIfNeeded())
    dispatch(fetchBankingProfile())
  }, [dispatch])

  return (
    <Card className="flex flex-col gap-4">
      <Heading variant={HeadingVariants.H2}>Plan & Billing</Heading>
      <div className="flex flex-col gap-8">
        <BillingTable />

        {isInActiveFreeTrial ? (
          <div id="reactivation">
            <Button
              variant="primary"
              onClick={() => {
                track('click purchase plan')
                navigate('/accounts/choose-plan')
              }}
            >
              Purchase a Heard Plan
            </Button>
          </div>
        ) : (
          <>
            {membershipIsCancelling && (
              <div
                id="reactivation"
                className="flex flex-wrap items-center gap-3"
              >
                <ReactivatePlanDialog />

                <Button
                  variant="secondary"
                  onClick={downloadZipFile}
                  loading={downloadingFiles}
                  disabled={downloadingFiles}
                >
                  Download Account Information
                </Button>

                <AccountDownloadDialog
                  open={downloadDialogOpen}
                  close={() => setDownloadDialogOpen(false)}
                />
              </div>
            )}

            {!membershipIsCancelling && (
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  onClick={() => {
                    track('click change plan')
                    goToPlanChange()
                  }}
                >
                  Change Plan
                </Button>

                {canSelfServeCancel && (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      handleSelfServeCancellation()
                    }}
                    disabled={isLoadingTransactionsAndUserTaxEstimates}
                  >
                    Cancel Plan
                  </Button>
                )}

                {!canSelfServeCancel && !membershipHasManualRequest && (
                  <ConfirmManualCancellationDialog
                    hasContractorFilingInProgress={
                      hasContractorFilingInProgress
                    }
                  />
                )}
              </div>
            )}

            <PaymentMethodBlock />
            <InvoiceList />
            <Button
              variant="link"
              size="sm"
              className="h-auto self-start whitespace-normal px-0 text-left"
              onClick={redirectToStripePortal}
            >
              Manage saved payment methods and view all invoices
            </Button>
          </>
        )}
        {planServices.length > 0 && (
          <>
            <Separator />
            <div className="flex flex-col gap-2">
              <Text variant={TypographyStyles.EYEBROW}>Plan Services</Text>
              <ul className="list-disc pl-4">
                {planServices.map((label) => (
                  <li key={label}>
                    <Text variant={TypographyStyles.BODY_MD}>{label}</Text>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </div>
    </Card>
  )
}
